import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { getReturnUrl, redirectAfterAuth } from "@/lib/authRedirect";

export const useAuthRedirect = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Check for auth parameters in the URL
  const hasAuthParams = () => {
    return searchParams.has("access_token") || 
           searchParams.has("refresh_token") || 
           searchParams.has("provider_token") || 
           searchParams.has("code");
  };

  // Helper function to check if user has completed onboarding
  const redirectLoggedInUser = async (userId: string) => {
    try {
      setIsLoading(true);
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('profession')
        .eq('id', userId)
        .single();
      
      if (error) {
        throw error;
      }
      
      // If profession is not set, redirect to simple-onboarding
      if (!profile.profession) {
        navigate("/simple-onboarding", { replace: true });
      } else {
        // Use redirectAfterAuth to handle redirection
        redirectAfterAuth(navigate);
      }
    } catch (error) {
      console.error("Error checking profile:", error);
      navigate("/simple-onboarding", { replace: true }); // Default to simple-onboarding if there's an error
    } finally {
      setIsLoading(false);
    }
  };

  // Check for existing session on page load
  useEffect(() => {
    let isMounted = true;
    
    // Check if there are auth-related parameters in the URL
    if (hasAuthParams()) {
      console.log("Detected auth params in Auth page, redirecting to auth/callback for handling");
      navigate("/auth/callback" + window.location.search, { replace: true });
      return;
    }
    
    // Check if user is already logged in
    const checkExistingSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && isMounted) {
          console.log("Found existing session in Auth page, redirecting user to dashboard");
          await redirectLoggedInUser(session.user.id);
        } else {
          setIsCheckingAuth(false);
        }
      } catch (error) {
        console.error("Error checking session:", error);
        setIsCheckingAuth(false);
      }
    };
    
    checkExistingSession();
    
    return () => {
      isMounted = false;
    };
  }, [navigate, searchParams]);

  return {
    isLoading,
    setIsLoading,
    redirectLoggedInUser,
    hasAuthParams,
    isCheckingAuth
  };
};
